import React, { ElementType, useEffect, useRef, useState } from 'react';
import { Location } from 'history';
import { isAndroid, isMobile } from 'react-device-detect';
import QrReader from 'react-qr-reader';
import {
  GeoZone,
  HubSortInstruction,
  Region,
  RegionCode,
} from '../../types/orderTypes';
import { supportsWebcam } from '../../utils/supportsWebcam';
import { fetchHubSortScan } from './HubUtils';
import './PreSortScanner.scss';
import { Helmet } from 'react-helmet';

const geoZoneToGeoZoneDescription = {
  '': 'Unknown',
  [GeoZone.OSLO_BAERUM]: 'Oslo-Bærum',
  [GeoZone.NORTH]: 'Oslo North',
  [GeoZone.EAST]: 'Oslo East',
  [GeoZone.WEST]: 'Oslo West',
  [GeoZone.BERGEN]: 'Bergen',
  [GeoZone.VESTFOLD]: 'Vestfold',
  [GeoZone.STAVANGER]: 'Stavanger',
  [GeoZone.TRONDHEIM]: 'Trondheim',
};

const geoZoneCodeColor = {
  [GeoZone.OSLO_BAERUM]: {
    background: '#F2527D',
    text: '#fff',
  },
  [GeoZone.NORTH]: {
    background: '#61177C',
    text: '#fff',
  },
  [GeoZone.EAST]: {
    background: '#33A002',
    text: '#fff',
  },
  [GeoZone.WEST]: {
    background: '#1DB6F2',
    text: '#fff',
  },
  [GeoZone.BERGEN]: {
    background: '#1DB6F2',
    text: '#fff',
  },
  [GeoZone.TRONDHEIM]: {
    background: '#61177C',
    text: '#fff',
  },
  [GeoZone.STAVANGER]: {
    background: '#33A002',
    text: '#fff',
  },
  [GeoZone.VESTFOLD]: {
    background: '#F2F2F2',
    text: '#000',
  },
};

const regionCodeColor = {
  OSL: {
    background: '#F2527D',
    text: '#fff',
  },
  TRD: {
    background: '#61177C',
    text: '#fff',
  },
  SVG: {
    background: '#33A002',
    text: '#fff',
  },
  BGO: {
    background: '#1DB6F2',
    text: '#fff',
  },
  VFO: {
    background: '#F2F2F2',
    text: '#000',
  },
};

function ShowGeoZone({ scan }: { scan: HubSortInstruction }): JSX.Element {
  const geoZoneCode: string =
    geoZoneToGeoZoneDescription[scan?.geoZone?.toString()];

  const geoZoneColor: { background: string; text: string } =
    geoZoneCodeColor[scan?.geoZone?.toString()];

  if (geoZoneCode) {
    return (
      <div
        className="regionInfo"
        style={{
          backgroundColor: geoZoneColor.background,
          color: geoZoneColor.text,
        }}
      >
        {geoZoneCode}
      </div>
    );
  } else {
    return <> </>;
  }
}

function ShowRegionZone({ scan }: { scan: HubSortInstruction }): JSX.Element {
  const regionCode: string = RegionCode[scan?.orderRegion];

  const regionColor: { background: string; text: string } =
    regionCodeColor[regionCode];

  if (regionCode) {
    return (
      <div
        className="regionInfo"
        style={{
          backgroundColor: regionColor.background,
          color: regionColor.text,
        }}
      >
        {regionCode}
      </div>
    );
  } else {
    return <> </>;
  }
}

function ShowBothZones({ scan }: { scan: HubSortInstruction }): JSX.Element {
  return (
    <>
      <ShowGeoZone scan={scan} />
      <ShowRegionZone scan={scan} />
    </>
  );
}

export function GeoZonePreSortScanner({
  region,
  location,
}: {
  region: Region;
  location: Location;
}): JSX.Element {
  return (
    <PreSortScanner
      region={region}
      ShowFeedback={ShowGeoZone}
      location={location}
    />
  );
}

export function RegionZonePreSortScanner({
  region,
  location,
}: {
  region: Region;
  location: Location;
}): JSX.Element {
  return (
    <PreSortScanner
      region={region}
      ShowFeedback={ShowRegionZone}
      location={location}
    />
  );
}

export function ShowBothZonesPreSortScanner({
  region,
  location,
}: {
  region: Region;
  location: Location;
}): JSX.Element {
  return (
    <PreSortScanner
      region={region}
      ShowFeedback={ShowBothZones}
      location={location}
    />
  );
}

export default function PreSortScanner({
  region,
  ShowFeedback,
  location,
}: {
  region: Region;
  ShowFeedback: ElementType;
  location: Location;
}): JSX.Element {
  const [inputValue, setInputValue] = useState<string>('');
  const [scannedOrderId, setScannedOrderId] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [inputIsInFocus, setInputIsInFocus] = useState<boolean>(false);
  const [scan, setScan] = useState<HubSortInstruction>(null);
  const [scannerType, setScannerType] = useState<'laser' | 'qr'>(
    (isMobile && isAndroid) || !supportsWebcam ? 'laser' : 'qr'
  );
  const inputRef = useRef<HTMLInputElement>(null);

  const getOrderData = async (id: string, parcelId?: string): Promise<void> => {
    setScan(await fetchHubSortScan(id, parcelId, true));
  };

  useEffect(() => {
    if (inputRef) {
      inputRef.current.focus();
      setInputIsInFocus(true);
    }
  }, [inputRef]);

  const handleQRScan = (data: any): void => {
    setError('');
    setScan(null);
    if (data) {
      try {
        const parsedData = JSON.parse(data);
        setInputValue('');
        setScannedOrderId(parsedData.ordegrId);
        getOrderData(parsedData.orderId, parsedData.parcelId);
        inputRef.current.focus();
      } catch (e) {
        setError("Not valid QR code. Can't scan package");
        console.error(e);
      }
    }
  };

  const handleInputChange = (value: string): void => {
    setError('');
    setScan(null);
    try {
      const parsedData = JSON.parse(value);
      setInputValue('');
      getOrderData(parsedData.orderId);
      setScannedOrderId(parsedData.orderId);
      inputRef.current.focus();
    } catch (e) {
      setError("Not valid order number. Can't scan package");
      console.error(e);
    }
    setInputValue(value);
    getOrderData(value);
  };

  const locationTitleRender = () => {
    switch (true) {
      case location.pathname.includes('reg'):
        return '- Region';
      case location.pathname.includes('geo'):
        return '- Geo';
      default:
        return '';
    }
  };
  return (
    <div className="preSortScanner">
      <Helmet>
        <title>Pre-Sort Scanner {locationTitleRender()}</title>
      </Helmet>
      <div className="content">
        <div className="scannerResultContainer">
          {!inputIsInFocus && scannerType === 'laser' && (
            <div
              className="tapToReactivate"
              onClick={() => inputRef.current.focus()}
            >
              Tap to activate laser scanner
            </div>
          )}
          {!scan && <div>Scan package to see region or cage row</div>}
          {scan && (
            <>
              <ShowFeedback scan={scan} />

              {scan.shelfId && (
                <div>
                  <div style={{ textAlign: 'center' }}>Cage row</div>
                  <div style={{ fontSize: 150, fontWeight: 'bold' }}>
                    {scan.shelfId?.replace(/\d/g, '')}
                  </div>
                </div>
              )}
              {!scan.shelfId && <div>No shelf ID</div>}
              {!scan.orderPresent && <div>Can't find order</div>}
              <div>{scannedOrderId}</div>
            </>
          )}
        </div>
        {error && <div className="error">{error}</div>}
        <div className="inputArea">
          <input
            type="tel"
            value={inputValue}
            onChange={e => handleInputChange(e.target.value)}
            onBlur={() => setInputIsInFocus(false)}
            onFocus={() => setInputIsInFocus(true)}
            ref={inputRef}
          />
          {isMobile && isAndroid && (
            <button
              onClick={() =>
                setScannerType(cs => (cs === 'qr' ? 'laser' : 'qr'))
              }
            >
              Use {scannerType === 'qr' ? 'laser' : 'QR'}
            </button>
          )}
        </div>
        {scannerType === 'qr' ? (
          <QrReader
            delay={300}
            onError={() => {}}
            onScan={handleQRScan}
            style={{ width: '100%' }}
          />
        ) : (
          <div />
        )}
      </div>
    </div>
  );
}
